import { PostReach } from '@shared/posting/outboxStatus';

/**
 * N5 (86d3w2kg2) — the wire contract of the Notification Center, mirrored verbatim.
 *
 * Backend source of truth: `CSC_CORE/Features/NotificationCenter/Objects/DTOs/`. The FE cannot tell
 * which of these fields are stored and which the server derives at read — and must not try. `state`,
 * `reach`, `attempt`, `maxAttempts` and `nextRetryAt` are all recomputed server-side on every read
 * from the live outbox job, which is why one of them (`retrying → queued`) can change with no
 * database write for any mechanism to observe.
 */

/** Which slice of the inbox. Mirrors `NotificationTab` and the repository's `ApplyTab` switch. */
export type NotificationTab = 'all' | 'needs' | 'unread' | 'done';

/**
 * Where the posting is in its journey, as the user reads it. Server-derived on every read.
 *
 * ⚠️ A plain string, not a C# enum on the wire. `Program.cs` has the solution's only
 * `AddControllers()` and adds no `JsonStringEnumConverter`, so an enum would arrive as an opaque
 * integer — the backend declares these as `string` for exactly that reason.
 */
export type NotificationState =
  | 'queued'
  | 'posting'
  | 'retrying'
  | 'posted'
  | 'failed'
  | 'reconcile'
  | 'cancelled';

/** How far the post got toward NetSuite. Null when nobody established it. */
export type NotificationReach = 'not_reached' | 'ambiguous' | 'reached' | null;

/**
 * A recovery verb the SERVER permits for this row, right now.
 *
 * ⛔ Never recomputed client-side from `reach`. `PostingJourney.Actions` is the single server-side
 * implementation that both renders these and gates the endpoint, so what the user sees and what the
 * server permits cannot disagree. Recomputing here would reintroduce exactly that gap — and the
 * failure mode is a duplicate record in the live company file.
 */
export type NotificationAction = 'retry' | 'cancel' | 'reconcile';

/** One step in the posting's story. Frozen at settle — the scrub destroys the originals. */
export interface TimelineEntry {
  at: string;
  label: string;
  state: string | null;
  detail: string | null;
}

export interface Notification {
  id: number;

  /**
   * The posting job behind this row, when there is one.
   *
   * ⛔ NOT a store key. It is nullable by schema — comment, procurement and system notifications
   * carry none — and a nullable column cannot key a collection. The store keys on `id`.
   */
  jobId: number | null;

  /** Display only. ⛔ Never sort or compare on this — see `createdAtMicros`. */
  createdAt: string;

  /**
   * THE ordering key, and the only one. Microseconds since the epoch, matching the server's
   * `ORDER BY (CreatedAt DESC, Id DESC)` exactly under pure integer math.
   *
   * ⛔ Two traps this exists to close. `System.Text.Json` trims trailing fractional zeros, so
   * comparing `createdAt` strings sorts `"…:05Z"` AFTER `"…:05.5Z"` (`'Z'` > `'.'`) — backwards. And
   * `Date.parse` truncates to milliseconds while the server orders at microseconds, so two rows in
   * the same millisecond compare equal and shuffle.
   */
  createdAtMicros: number;

  unread: boolean;
  needsAction: boolean;
  resolved: boolean;
  done: boolean;

  category: string;
  /** The posting flow — `posting.so` | `posting.cl` | `posting.mis` | `posting.ir`. */
  type: string;
  state: NotificationState;
  reach: NotificationReach;
  failureClass: string | null;

  /**
   * What the server permits, as a plain string array: `["retry","cancel"]`, `["reconcile"]`, `[]`.
   *
   * ⛔ NOT a bitfield. The `[Flags] NotificationActions` enum is internal to `PostingJourney`
   * server-side and never reaches the wire; there is deliberately no TypeScript mirror of it. Test
   * membership with the helpers below — no bit masks, no `& 1`.
   */
  actions: NotificationAction[];

  title: string;
  body: string | null;
  entityType: string | null;
  entityId: string | null;
  entityRef: string | null;
  nsNumber: string | null;
  errorSummary: string | null;
  suggestion: string | null;

  attempt: number;
  maxAttempts: number;
  nextRetryAt: string | null;
  readAt: string | null;

  timeline: TimelineEntry[];
}

/**
 * Whole-inbox totals. ⚠️ ASSIGNED from the server, never derived from a loaded page — a page is a
 * window and a badge is a total. A user with 200 unread rows and a 50-row window would otherwise
 * show a badge of 50.
 */
export interface NotificationCounts {
  needsCount: number;
  unreadCount: number;
  activeCount: number;
  doneCount: number;
}

/**
 * An opaque keyset cursor (`v1|micros|id`, base64url).
 *
 * ⛔ Never parse, construct, or compare one client-side. It is echoed back verbatim. That opacity is
 * what lets the server change the key's shape without a client release.
 */
export type Cursor = string;

export interface NotificationPage {
  items: Notification[];

  /** Emitted only when the `Take(pageSize + 1)` sentinel fired AND the page is non-empty. */
  nextCursor: Cursor | null;

  /**
   * Invariant the server maintains: `hasMore === (nextCursor !== null)`. It comes from a sentinel
   * row, never from `items.length === pageSize` — which reports true on an exact multiple and sends
   * the client after a page that returns nothing.
   */
  hasMore: boolean;

  /**
   * The complete unresolved needs-action set, capped at 50, INDEPENDENT of the paged window. A
   * failed posting from three weeks ago is still pinned and cannot be derived from a recency window.
   */
  pinned?: Notification[];
  pinnedTruncated: boolean;

  /** Present only when `includeCounts=true` was asked for. */
  counts?: NotificationCounts;

  /** The clock relative copy renders against ("next retry in 6m") — not the client's, which skews. */
  serverTimeUtc: string;
}

/** The body of a recovery response — carried on BOTH the 200 and the 400 branch. */
export interface NotificationActionResult {
  notification: Notification;
  counts: NotificationCounts;
}

export interface GetNotificationsParams {
  tab?: NotificationTab;
  cursor?: Cursor;
  pageSize?: number;
  includeCounts?: boolean;
  includePinned?: boolean;
}

// ── Action helpers ───────────────────────────────────────────────────────────
//
// Three one-liners rather than bit maths. Call them in-component on the directly-subscribed row —
// ⛔ never behind a zustand getter that reads `get()` internally, which is the 86d3d1z40 shape: the
// React Compiler memoizes on the stable function reference and the button freezes in its
// first-rendered enabled state while the row morphs around it.

export const canRetry = (n: Notification): boolean => n.actions.includes('retry');
export const canCancel = (n: Notification): boolean => n.actions.includes('cancel');
export const canReconcile = (n: Notification): boolean => n.actions.includes('reconcile');

/**
 * Maps the wire's reach string onto the numeric `PostReach` the shared posting helpers already
 * speak, so `mayHaveReachedNetSuite` and friends apply unchanged.
 *
 * ⛔ For COPY AND BADGES ONLY. Recovery-button availability comes from `actions` — see the note on
 * that field.
 */
export function reachToNumeric(reach: NotificationReach): PostReach {
  switch (reach) {
    case 'not_reached':
      return PostReach.NotReached;
    case 'ambiguous':
      return PostReach.Ambiguous;
    case 'reached':
      return PostReach.Reached;
    default:
      return PostReach.Unknown; // Nobody established it.
  }
}
